export type HazardLayerFolder =
  | 'Wildfires'
  | 'Hurricanes'
  | 'Severe Weather'
  | 'Winter Weather'
  | 'Flooding';

export type HazardPortalLayer = {
  id: string;
  name: string;
  folder: HazardLayerFolder;
  description: string;
  arcgisItemId: string;
  serviceUrl?: string | null;
  itemType: 'Feature Service' | 'Map Service' | 'Web Map';
  defaultVisible: boolean;
};

/**
 * Living Atlas / NOAA / NIFC portal items seeded as hub layers.
 *
 * Shared feeds (watches, radar, gauges, precip, NWM anomaly, QPE) are filed
 * once, in the most relevant folder. National Water Model Maximum Flow
 * (Hourly Forecast) is omitted because the source list had no item id.
 */
export const HAZARD_PORTAL_LAYER_CATALOG: HazardPortalLayer[] = [
  {
    id: 'nifc-usa-current-wildfires',
    name: 'USA Current Wildfires (NIFC)',
    folder: 'Wildfires',
    description:
      'NIFC incident points and perimeters for wildfires, prescribed fires, and complexes, sourced from IRWIN and IMTs.',
    arcgisItemId: 'd957997ccee7408287a963600a77f61f',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'viirs-thermal-hotspots',
    name: 'Satellite (VIIRS) Thermal Hotspots and Fire Activity',
    folder: 'Wildfires',
    description: 'VIIRS satellite thermal hotspots and fire activity detections.',
    arcgisItemId: 'dece90af1a0242dcbf0ca36d30276aa3',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'current-weather-wind-stations',
    name: 'Current Weather and Wind Station Data',
    folder: 'Wildfires',
    description: 'Current weather and wind observations from METAR and related stations.',
    arcgisItemId: 'cb1886ff0a9d4156ba4d2fadd7e8a139',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'recent-air-quality',
    name: 'Recent Air Quality Conditions',
    folder: 'Wildfires',
    description: 'Recent air quality observations used during smoke and fire response.',
    arcgisItemId: '8dcf5d4e124f480fa8c529fbe25ba04e',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nws-smoke-forecast',
    name: 'National Weather Service Smoke Forecast',
    folder: 'Wildfires',
    description: 'NWS smoke forecast for wildfire and prescribed-fire operations.',
    arcgisItemId: 'a98fd08751a5480c898b7cebe38807f4',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nws-wind-gust-forecast',
    name: 'National Weather Service Wind Gust Forecast',
    folder: 'Wildfires',
    description: 'NWS wind-gust forecast used for fire weather planning.',
    arcgisItemId: '0bf37bd236e848cf8b4a6dc5b162c11a',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'alertcalifornia-live-cameras',
    name: 'ALERTCalifornia Live Cameras',
    folder: 'Wildfires',
    description: 'ALERTCalifornia live camera locations for wildfire situational awareness.',
    arcgisItemId: '0c1c1166688f4f2db938194799337be4',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'active-hurricanes-tropical-cyclones',
    name: 'Active Hurricanes / Tropical Cyclones (Global)',
    folder: 'Hurricanes',
    description: 'Active global tropical cyclones with forecast tracks and cones.',
    arcgisItemId: '248e7b5827a34b248647afb012c58787',
    itemType: 'Web Map',
    defaultVisible: false,
  },
  {
    id: 'active-hurricanes-alt-symbology',
    name: 'Active Hurricanes / Tropical Cyclones (Alternative Symbology)',
    folder: 'Hurricanes',
    description:
      'Active global tropical cyclones with updated alternative cartography.',
    arcgisItemId: '19d3a0c8a51b44179fbf7b3a7489cb65',
    itemType: 'Web Map',
    defaultVisible: false,
  },
  {
    id: 'nhc-tropical-summary-storm-surge',
    name: 'NHC Tropical Weather Summary with Storm Surge Inundation',
    folder: 'Hurricanes',
    description:
      'National Hurricane Center tropical weather summary including storm-surge inundation.',
    arcgisItemId: 'f55a8cead7b74f9ba8b62e90e1027774',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'potential-storm-surge-flooding-tiled',
    name: 'Potential Storm Surge Flooding (Tiled)',
    folder: 'Hurricanes',
    description: 'Tiled map service of potential storm-surge flooding.',
    arcgisItemId: 'cdc29af5c53249128525c7eaac866750',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nws-post-event-damage-assessments',
    name: 'NOAA NWS Post-Event Damage Assessments',
    folder: 'Severe Weather',
    description: 'NOAA National Weather Service post-event damage assessments.',
    arcgisItemId: 'b6b9dd55450a4f64bcafb3cd6e005a5c',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'spc-weather-outlooks',
    name: 'NOAA Storm Prediction Center Weather Outlooks',
    folder: 'Severe Weather',
    description: 'SPC convective outlooks for severe thunderstorms and tornadoes.',
    arcgisItemId: 'e17e0e91afbf4fc8b73215bb37dc3bc3',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'usa-storm-reports',
    name: 'USA Storm Reports',
    folder: 'Severe Weather',
    description: 'Local storm reports of hail, wind, and tornadoes.',
    arcgisItemId: 'e109e8fd9c5a495c813b5cbaee9c7d9b',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'usa-short-term-weather-warnings',
    name: 'USA Short-Term Weather Warnings',
    folder: 'Severe Weather',
    description: 'Short-term National Weather Service warnings.',
    arcgisItemId: 'bba3d0a070d34cea9a303c24a71d5190',
    itemType: 'Web Map',
    defaultVisible: false,
  },
  {
    id: 'usa-weather-watches-warnings',
    name: 'USA Weather Watches and Warnings',
    folder: 'Severe Weather',
    description:
      'Active USA weather watches and warnings, including Red Flag warnings. Shared across fire, tropical, and flood workflows.',
    arcgisItemId: 'a6134ae01aad44c499d12feec782b386',
    itemType: 'Web Map',
    defaultVisible: false,
  },
  {
    id: 'noaa-weather-radar-current',
    name: 'NOAA Weather Radar',
    folder: 'Severe Weather',
    description: 'Updated NOAA weather radar. Shared with flood operations.',
    arcgisItemId: '992fdc2afc224242b2c31a7f3c108064',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'noaa-weather-radar-time-enabled',
    name: 'NOAA Weather Radar (Time Enabled)',
    folder: 'Severe Weather',
    description: 'Time-enabled NOAA weather radar loops.',
    arcgisItemId: 'b0cdf263cea24544b0da2fc00fb2b259',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nws-3-day-minmax-temperature',
    name: 'National Weather Service 3-Day Min/Max Temperature Forecast',
    folder: 'Severe Weather',
    description: 'NWS three-day minimum and maximum temperature forecast.',
    arcgisItemId: '0ae7cf18df0a4b4d9e7eea665f00500d',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'cpc-us-hazards-drought-wildfire',
    name: 'CPC U.S. Hazards / Drought / Wildfire',
    folder: 'Severe Weather',
    description: 'Climate Prediction Center U.S. hazards, drought, and wildfire outlooks.',
    arcgisItemId: 'd7a5d1bb27394181888cd50de0dd4838',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'wpc-wssi',
    name: 'WPC Winter Storm Severity Index (WSSI)',
    folder: 'Winter Weather',
    description: 'Weather Prediction Center Winter Storm Severity Index.',
    arcgisItemId: 'e53609341b3646e9b565408cbdb25dbc',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'wpc-wssi-probabilistic',
    name: 'WPC Winter Storm Severity Index Probabilistic (WSSI-P)',
    folder: 'Winter Weather',
    description: 'Probabilistic Winter Storm Severity Index from the Weather Prediction Center.',
    arcgisItemId: '3170144cc4154292b48dd13157032d69',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nws-snowfall-forecast',
    name: 'National Weather Service Snowfall Forecast',
    folder: 'Winter Weather',
    description: 'Living Atlas NWS snowfall forecast.',
    arcgisItemId: 'be1bb766bf1c44a9be97bbb7c04355ff',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nws-ice-accumulation-forecast',
    name: 'National Weather Service Ice Accumulation Forecast',
    folder: 'Winter Weather',
    description: 'Living Atlas NWS ice accumulation forecast.',
    arcgisItemId: '0a279bc085264a45b1c48ad6b60afd8d',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'precipitation-forecast-us',
    name: 'Precipitation Forecast (US)',
    folder: 'Flooding',
    description: 'U.S. precipitation forecast. Shared with tropical operations.',
    arcgisItemId: 'f9e9283b9c9741d09aad633f68758bf6',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'live-stream-gauges-flooding',
    name: 'Live Stream Gauges (Flooding)',
    folder: 'Flooding',
    description: 'Live USGS/NWS stream gauges used for flood monitoring.',
    arcgisItemId: '81c5a9f2a2704d54a49042a44eefa5d3',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'rfc-max-status-forecast-trends',
    name: 'River Forecast Center Max Status Forecast Trends',
    folder: 'Flooding',
    description:
      'RFC forecast trend with triangles pointing up or down for rising or falling rivers.',
    arcgisItemId: '109ee637b8fa4d729d33e326717aecf3',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nws-latest-flood-inundation-extent',
    name: 'Latest Flood Inundation Extent',
    folder: 'Flooding',
    description:
      'NWS Flood Inundation Mapping latest extent. Coverage is about 60% of the U.S.',
    arcgisItemId: '97932846d7474d458c14518c26467fa3',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nwm-5-day-forecast-conus',
    name: 'National Water Model 5-Day Forecast (CONUS)',
    folder: 'Flooding',
    description: 'National Water Model five-day streamflow forecast for CONUS.',
    arcgisItemId: '791952cc7de44084b5af78b94aacc2eb',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'qpe-surface-met-hydro',
    name: 'Quantitative Precipitation Estimates (QPE)',
    folder: 'Flooding',
    description:
      'Surface meteorological and hydrologic analyses — quantitative precipitation estimates.',
    arcgisItemId: '06d2bf3540a541ce8c8e61fcbe71d5d5',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'qpf-quantitative-precipitation-forecast',
    name: 'Quantitative Precipitation Forecast (QPF)',
    folder: 'Flooding',
    description: 'NOAA quantitative precipitation forecast.',
    arcgisItemId: 'c1e3bc96340b4468bb9b1388302014f6',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'flood-hazards-outlook',
    name: 'Flood Hazards Outlook',
    folder: 'Flooding',
    description: 'National flood hazards outlook.',
    arcgisItemId: '25fddce081224d7192343cfa46fff065',
    itemType: 'Feature Service',
    defaultVisible: false,
  },
  {
    id: 'nwm-hourly-anomaly-forecast',
    name: 'National Water Model (Hourly Anomaly Forecast)',
    folder: 'Flooding',
    description: 'National Water Model hourly streamflow anomaly forecast.',
    arcgisItemId: 'cadb9f7713ad443f85aa34a01ddb7785',
    itemType: 'Map Service',
    defaultVisible: false,
  },
  {
    id: 'nwm-maximum-flow-10-day',
    name: 'National Water Model Maximum Flow (10 Day Forecast)',
    folder: 'Flooding',
    description: 'National Water Model maximum flow over a 10-day forecast.',
    arcgisItemId: '50500544fa934eb582b7701217c2e6fa',
    itemType: 'Map Service',
    defaultVisible: false,
  },
];

export const HAZARD_LAYER_FOLDERS: HazardLayerFolder[] = [
  'Wildfires',
  'Hurricanes',
  'Severe Weather',
  'Winter Weather',
  'Flooding',
];

export const SHARED_HAZARD_ITEM_IDS = [
  'a6134ae01aad44c499d12feec782b386',
  '992fdc2afc224242b2c31a7f3c108064',
  '81c5a9f2a2704d54a49042a44eefa5d3',
  'f9e9283b9c9741d09aad633f68758bf6',
  'cadb9f7713ad443f85aa34a01ddb7785',
  '06d2bf3540a541ce8c8e61fcbe71d5d5',
] as const;
